import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { useResourceUpdatedWatchable } from '../watchables/use-resource-updated-watchable';

export type WorkflowTriggerSummary = {
  triggerId: string;
  name: string;
  schedule: string | null;
  kind: string;
  /** How many workflows are bound to this trigger (>1 means shared). */
  sharedCount: number;
};

export type WorkflowSummary = {
  triggers: WorkflowTriggerSummary[];
  lastRunAt: Date | null;
  lastRunStatus: string | null;
};

/** Derived display state for the workflows-list status column. */
export type WorkflowDisplayStatus = 'active' | 'error' | 'ready';

export function workflowDisplayStatus(
  summary: WorkflowSummary | undefined
): WorkflowDisplayStatus {
  if (summary === undefined) {
    return 'ready';
  }
  if (summary.triggers.length === 0) {
    return 'ready';
  }
  if (summary.lastRunStatus === 'failed') {
    return 'error';
  }
  return 'active';
}

/** Compact "2 hours ago" style label; "Never" when there's no run. */
export function formatLastRun(date: Date | null): string {
  if (date === null) {
    return 'Never';
  }
  return DateTime.fromJSDate(date).toRelative() ?? '—';
}

/** Stable disabled value — no fetch, and `loaded` so `isLoading` stays false. */
const DISABLED_SUMMARIES_STATE: {
  summaries: Map<string, WorkflowSummary>;
  loaded: boolean;
} = { summaries: new Map(), loaded: true };

/**
 * Batch-fetches enriched list data (triggers + shared count + last run) for the
 * given workflows in one request, keyed on the id set so each page fetches once.
 *
 * Backed by a `ResourceUpdated` event watchable: a run finishing or a binding
 * changing streams the new summaries into the list without a manual refetch.
 * Pass `enabled: false` to skip the fetch/subscription entirely.
 */
export function useWorkflowSummaries(
  workflowIds: string[],
  enabled = true
): {
  summaries: Map<string, WorkflowSummary>;
  /** True until the batch has resolved — render placeholders, not a status. */
  isLoading: boolean;
} {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const idsKey = useMemo(
    () => [...workflowIds].sort().join(','),
    [workflowIds]
  );
  const hasIds = workflowIds.length > 0;

  const state = useResourceUpdatedWatchable(
    `${orgId}:${idsKey}`,
    (factory) =>
      factory.makeWorkflowSummariesWatchable({ client, workflowIds }),
    { enabled, disabledValue: DISABLED_SUMMARIES_STATE }
  );

  return useMemo(
    () => ({ summaries: state.summaries, isLoading: hasIds && !state.loaded }),
    [state.summaries, state.loaded, hasIds]
  );
}
